import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Users } from "lucide-react";

export default function Employees() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", department_id: "" });
  const [saving, setSaving] = useState(false);

  const { data: employees = [], isLoading } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const { data: departments = [] } = useQuery({ queryKey: ["departments"], queryFn: () => api.get("/departments").then((r) => r.data) });
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Nombre y correo son obligatorios");
    setSaving(true);
    try {
      await api.post("/employees", { ...form, password: form.password || "funcionario123" });
      toast.success("Funcionario creado");
      qc.invalidateQueries({ queryKey: ["employees"] });
      setForm({ name: "", email: "", password: "", department_id: "" });
      setOpen(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (emp) => {
    await api.put(`/employees/${emp.id}`, { active: !emp.active });
    qc.invalidateQueries({ queryKey: ["employees"] });
    toast.success(emp.active ? "Funcionario desactivado" : "Funcionario activado");
  };

  return (
    <div className="space-y-5" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Funcionarios</h1>
          <p className="text-sm text-slate-500">{employees.length} funcionarios registrados</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-slate-900 font-bold hover:bg-slate-800" data-testid="add-employee-btn">
              <UserPlus className="mr-1 h-4 w-4" /> Nuevo
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="employee-dialog">
            <DialogHeader><DialogTitle className="font-heading">Nuevo funcionario</DialogTitle><DialogDescription>Crea una cuenta de funcionario para tu empresa.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="emp-name" /></div>
              <div><Label>Correo</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="emp-email" /></div>
              <div><Label>Contraseña (opcional)</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="funcionario123" className="mt-1" data-testid="emp-password" /></div>
              <div>
                <Label>Área</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="emp-area"><SelectValue placeholder="Selecciona área" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={create} disabled={saving} className="w-full rounded-full bg-slate-900 font-bold" data-testid="save-employee-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear funcionario"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : employees.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white py-16 text-slate-400">
          <Users className="mb-2 h-8 w-8" /><p className="text-sm">Aún no hay funcionarios</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {employees.map((e) => (
            <div key={e.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`employee-${e.id}`}>
              <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-slate-200 font-bold text-slate-600">
                {e.avatar ? <img src={e.avatar} alt="" className="h-full w-full object-cover" /> : e.name[0]}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-heading font-bold text-slate-900">{e.name}</div>
                <div className="truncate text-xs text-slate-400">{e.email}</div>
                <div className="mt-0.5 text-xs font-medium text-slate-500">{deptName(e.department_id)}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Switch checked={e.active} onCheckedChange={() => toggleActive(e)} data-testid={`toggle-${e.id}`} />
                <span className={`text-[10px] font-semibold ${e.active ? "text-emerald-600" : "text-slate-400"}`}>{e.active ? "Activo" : "Inactivo"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
